import { useEffect, useMemo, useState } from 'react';
import './css/Acp.css';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { Link, useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import Pagination10 from './components/Pagination10';
import DefaultModal from './components/modals/DefaultModal';
import usePagination10 from './components/hooks/usePagination10';
import usePermissions from './components/hooks/usePermissions';
import PageCard from './components/PageCard';

function Pessoas() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('pessoas');
  
  const [pessoasOriginais, setPessoasOriginais] = useState([]);
  const [atualizar, setAtualizar] = useState(0);
  const [pessoaSelecionada, setPessoaSelecionada] = useState(null);
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [modalData, setModalData] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [filtro, setFiltro] = useState('');
  const showDefaultModal = (type, message) => {
    setModalData({ show: true, type: type, message: message });
  };
  const closeDefaultModal = () => {
    setModalData(prevModalData => ({ ...prevModalData, show: false }));
  };

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    setLoading(true);
    axios
      .get(`${API_BASE_URL.PESSOA}/all`, {
        params: { cnpjAssociacao: cnpj },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setPessoasOriginais(result.data);
        setPessoaSelecionada(null); // Limpar seleção ao atualizar dados
      })
      .catch(error => {
        console.error('Erro ao obter registros:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar, token, cnpj, navigate, podeAbrir, carregandoPermissoes]);

  // Filtro por nome ou CPF (sempre maiúsculo para nome)
  const pessoasFiltradas = pessoasOriginais.filter(p => {
    if (!filtro) return true;
    const filtroUpper = filtro.toUpperCase();
    return (
      (p.nome && p.nome.toUpperCase().includes(filtroUpper)) ||
      (p.cpf && p.cpf.includes(filtro))
    );
  });
  const totalItems = pessoasFiltradas.length;
  const { setPagination } = usePagination10(totalItems);
  const selectedPessoas = pessoasFiltradas.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  function handleDelete(c) {
    axios
      .delete(`${API_BASE_URL.PESSOA}/${c.cnpjAssociacao}/${c.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {
        setAtualizar(prev => prev + 1);
        showDefaultModal('success', 'O registro foi excluído com sucesso!');
      })
      .catch(error => {
        console.error('Erro ao excluir registro:', error);
        showDefaultModal('error', 'Erro ao excluir o registro.');
      });
  }

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard
      title="Pessoas"
      icon="bi-people"
      scrollable={false}
      footer={totalItems > 0 ? <Pagination10 {...setPagination} /> : null}
    >
      {!podeAbrir ? (
        <div className="alert alert-warning m-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Acesso Negado</strong>
          <p className="mb-0 mt-2">
            Você não tem permissão para acessar o módulo de Pessoas.
          </p>
          <p className="mb-0 mt-2">
            <small>Entre em contato com o administrador do sistema.</small>
          </p>
          <button 
            className="btn btn-sm btn-outline-secondary mt-3"
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Voltar
          </button>
        </div>
      ) : (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
          <button
            onClick={() => navigate('/pessoa')}
            className="btn btn-success btn-sm d-inline-flex align-items-center"
            disabled={!podeCriar}
            title={!podeCriar ? 'Você não tem permissão para criar pessoas' : 'Nova pessoa'}
          >
            <i className="bi bi-plus-lg me-1" />
            Novo
          </button>
          <button
            className="btn btn-primary btn-sm d-inline-flex align-items-center"
            disabled={!podeEditar || !pessoaSelecionada}
            onClick={() =>
              pessoaSelecionada &&
              navigate('/pessoa', { state: { pessoa: pessoaSelecionada } })
            }
            title={!podeEditar ? 'Você não tem permissão para editar' : !pessoaSelecionada ? 'Selecione uma pessoa' : 'Editar pessoa selecionada'}
          >
            <i className="bi bi-pencil me-1" />
            Editar
          </button>
          <button
            className="btn btn-info btn-sm d-inline-flex align-items-center"
            disabled={!podeEditar || !pessoaSelecionada}
            onClick={() =>
              pessoaSelecionada &&
              navigate('/pessoa_dependentes', { state: { pessoa: pessoaSelecionada } })
            }
            title={!podeEditar ? 'Você não tem permissão para gerenciar dependentes' : !pessoaSelecionada ? 'Selecione uma pessoa' : 'Gerenciar dependentes'}
          >
            <i className="bi bi-people-fill me-1" />
            Dependentes
          </button>
          <div className="vr mx-1" />
          <button
            className="btn btn-danger btn-sm d-inline-flex align-items-center"
            disabled={!podeExcluir || !pessoaSelecionada}
            onClick={() => pessoaSelecionada && handleDelete(pessoaSelecionada)}
            title={!podeExcluir ? 'Você não tem permissão para excluir' : !pessoaSelecionada ? 'Selecione uma pessoa' : 'Excluir pessoa selecionada'}
          >
            <i className="bi bi-trash me-1" />
            Excluir
          </button>

          <div className="ms-auto d-flex align-items-center">
            <div className="input-group" style={{ maxWidth: 300, height: 31 }}>
              <span className="input-group-text bg-white border-end-0 px-2" style={{ height: 31 }}>
                <i className="bi bi-search text-muted" style={{ fontSize: 13 }} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm border-start-0"
                style={{ textTransform: 'uppercase', height: 31, fontSize: 13 }}
                placeholder="Pesquisar por nome ou CPF..."
                value={filtro}
                onChange={e => setFiltro(e.target.value.toUpperCase())}
              />
              {filtro && (
                <button
                  className="btn btn-outline-secondary btn-sm border-start-0 d-flex align-items-center px-2"
                  style={{ height: 31 }}
                  onClick={() => setFiltro('')}
                  title="Limpar pesquisa"
                  tabIndex={-1}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: 11 }} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          className="table-responsive-sm shadow-sm mb-0 small"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
              <table className="table table-hover table-striped table-sm">
                <thead className="table-secondary sticky-top bg-light">
                  <tr>
                    <th className="px-2 align-middle" style={{ width: '5%' }}>
                      CPF
                    </th>
                    <th className="px-2 align-middle">Nome</th>
                    <th className="px-2 align-middle">Nome da Mãe</th>
                  </tr>
                </thead>
                <tbody>
                  {!isLoading &&
                    selectedPessoas.map(c => (
                      <tr
                        key={`${c.cnpjAssociacao}-${c.id}`}
                        className={
                          pessoaSelecionada?.cpf === c.cpf
                            ? 'table-primary'
                            : ''
                        }
                        style={{ cursor: 'pointer' }}
                        onClick={() => setPessoaSelecionada(c)}
                      >
                        <td className="px-2">{c.cpf}</td>
                        <td className="px-2">{c.nome}</td>
                        <td className="px-2">{c.nomeMae}</td>
                      </tr>
                    ))}
                  {!isLoading && pessoasFiltradas.length === 0 && (
                    <tr>
                      <td colSpan="3" className="text-center">
                        Nenhuma pessoa encontrada.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
        </div>
      )}
      <DefaultModal
        show={modalData.show}
        type={modalData.type}
        onClose={closeDefaultModal}
        message={modalData.message}
      />
    </PageCard>
  );
}

export default Pessoas;
